// src/utils/affiliate.js

const AFFILIATE_SESSION_KEY = 'affiliate_id'
const AFFILIATE_SESSION_REF = 'affiliate_ref'

/**
 * Liest ?ref=... aus der aktuellen URL.
 */
export function getRefFromUrl() {
	try {
		const params = new URLSearchParams(window.location.search)
		const ref = params.get('ref')
		return ref && ref.trim() ? ref.trim() : null
	} catch {
		return null
	}
}

/**
 * Entfernt den ref-Query-Param aus der URL (schönere URLs, keine doppelten Events).
 */
export function stripRefFromUrl() {
	try {
		const { pathname, search, hash } = window.location
		const sp = new URLSearchParams(search)
		if (!sp.has('ref')) return

		sp.delete('ref')
		const cleanSearch = sp.toString()
		const newHref = `${pathname}${cleanSearch ? `?${cleanSearch}` : ''}${hash}`

		// Nur ersetzen, wenn es sich wirklich ändert
		if (newHref !== `${pathname}${search}${hash}`) {
			window.history.replaceState({}, '', newHref)
		}
	} catch {
		/* no-op */
	}
}

/**
 * Fragt Strapi nach einem Affiliate-Link per UID-Feld "Identifikator".
 * Gibt die numerische Strapi-ID zurück oder null.
 *
 * Auth:
 * - Öffentlich (ohne Header): Public-Rolle braucht "find"-Rechte auf Affiliate-Link.
 * - Mit Token: env GATSBY_STRAPI_API_TOKEN setzen → Authorization: Bearer ...
 */
export async function fetchAffiliateIdByRef(
	ref,
	{ strapiBaseUrl, apiToken } = {}
) {
	if (!ref) return null
	if (!strapiBaseUrl) {
		console.warn('[Affiliate] strapiBaseUrl fehlt.')
		return null
	}

	let url
	try { url = new URL('/api/affiliate-links', strapiBaseUrl) }
	catch { return null }

	// Field names match the CMS schema; unknown attributes are rejected by Strapi.
	url.searchParams.set('filters[Identifikator][$eq]', ref)
	url.searchParams.set('fields[0]', 'id')
	url.searchParams.set('pagination[pageSize]', '1')

	const headers = { 'Content-Type': 'application/json' }
	if (apiToken) headers.Authorization = `Bearer ${apiToken}`

	try {
		const res = await fetch(url.toString(), { headers, credentials: 'omit' })
		if (!res.ok) {
			console.warn('[Affiliate] Strapi antwortete nicht OK:', res.status)
			return null
		}
		const json = await res.json()
		const first = Array.isArray(json?.data) ? json.data[0] : null
		const id = first?.id ?? null
		return typeof id === 'number' ? id : null
	} catch (e) {
		console.warn('[Affiliate] fetchAffiliateIdByRef Fehler:', e)
		return null
	}
}

/**
 * Hauptfunktion: liest ?ref, fragt Strapi, speichert ID in sessionStorage (pro Tab).
 * Überschreibt nur, wenn explizit ein ref in der URL ist (kein zufälliges Überschreiben).
 */
export async function detectAndStoreAffiliateRef(options = {}) {
	if (typeof window === 'undefined') return

	const ref = getRefFromUrl()
	if (!ref) return

	// Wenn bereits eine ID gespeichert ist und der gleiche ref nochmal kommt → no-op
	try {
		const existingRef = sessionStorage.getItem(AFFILIATE_SESSION_REF)
		if (existingRef === ref && sessionStorage.getItem(AFFILIATE_SESSION_KEY)) return
	} catch {
		return
	}

	const id = await fetchAffiliateIdByRef(ref, options)
	if (id) {
		try {
			sessionStorage.setItem(AFFILIATE_SESSION_KEY, String(id))
			sessionStorage.setItem(AFFILIATE_SESSION_REF, ref)
		} catch {
			return
		}
		// Optional: GA/Event
		try {
			window.dataLayer = window.dataLayer || []
			window.dataLayer.push({
				event: 'affiliate_ref_detected',
				affiliate_ref: ref,
				affiliate_id: id
			})
		} catch {
			/* no-op */
		}
	}
}
